/**
 * Cache Indicator Component
 * 
 * Shows when data was last updated with a refresh button.
 * Displays a subtle indicator that data may be cached.
 * 
 * Usage:
 *   <CacheIndicator 
 *     cacheKey="dashboard" 
 *     onRefresh={() => fetchData()} 
 *     loading={loading}
 *   />
 */

import React, { useState, useEffect } from 'react';
import { RefreshCw, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * Format time ago string
 */
const formatTimeAgo = (timestamp) => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 120) return '1 minute ago';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 7200) return '1 hour ago';
  return `${Math.floor(seconds / 3600)} hours ago`;
};

/**
 * Get cache timestamp from sessionStorage
 */
const getCacheTimestamp = (cacheKey) => {
  try {
    const cached = sessionStorage.getItem(`${cacheKey}_cache`);
    if (cached) {
      const { timestamp } = JSON.parse(cached);
      return timestamp;
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null;
};

export const CacheIndicator = ({ 
  cacheKey, 
  onRefresh, 
  loading = false,
  className = '',
  hidden = false,     // iter959: allow parents to gate visibility (e.g. NG on Home)
}) => {
  const [timestamp, setTimestamp] = useState(null);
  const [timeAgo, setTimeAgo] = useState('');

  // Get initial timestamp
  useEffect(() => {
    const ts = getCacheTimestamp(cacheKey);
    setTimestamp(ts);
    if (ts) {
      setTimeAgo(formatTimeAgo(ts));
    }
  }, [cacheKey]);

  // Update time ago every 30 seconds
  useEffect(() => {
    if (!timestamp) return;
    
    const interval = setInterval(() => {
      setTimeAgo(formatTimeAgo(timestamp));
    }, 30000);
    
    return () => clearInterval(interval);
  }, [timestamp]);

  // Update timestamp when loading completes
  useEffect(() => {
    if (!loading) {
      const ts = getCacheTimestamp(cacheKey);
      setTimestamp(ts);
      if (ts) {
        setTimeAgo(formatTimeAgo(ts));
      }
    }
  }, [loading, cacheKey]);

  // Don't show if no cache timestamp - or if the parent explicitly hides us.
  if (hidden) return null;
  if (!timestamp) return null;

  return (
    <div className={`flex items-center gap-2 text-xs text-muted-foreground ${className}`}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 cursor-default">
              <Clock className="w-3 h-3" />
              <span>Updated {timeAgo}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Data cached for faster navigation</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={onRefresh}
              disabled={loading}
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Refresh data</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default CacheIndicator;
